import React, { useState, useEffect, useContext } from 'react'
import { FaChevronLeft, FaChevronRight, FaHistory } from 'react-icons/fa'
import { useHistory } from 'react-router-dom'
import '../../../styles/content/ContentProgram.css'
import { fileSystemUrl } from '../../../constants/Connections'
import { useUser } from '../../../auth'
import { getById, getProgramHistory } from '../../../server/server'
import RunProgramModal from '../../modals/RunProgramModal'
import UrlPreview from '../../feedsAndDashboard/UrlPreview'
import ImagePreviewModal from '../../modals/ImagePreviewModal'
import ProgramHistoryModal from '../../modals/ProgramHistoryModal'
import ReactTooltip from 'react-tooltip'
import { format } from 'date-fns'
import ReadMoreText from '../../ReadMoreText'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { ReactComponent as TargetUndo } from '../../../assets/svg/undoTarget.svg'
import { ReactComponent as LastCreated } from '../../../assets/svg/lastCreated.svg'
import { ContentContext } from '../ContentContext'
import ShowMoreText from 'react-show-more-text'
import { capitaliseFirstLetter, scrollToTopOf } from '../../../utils/util'
import { ReactComponent as ContentPostIcon } from '../../../assets/svg/ContentPostIcon.svg'
import { ReactComponent as ContentQuestionIcon } from '../../../assets/svg/ContentQuestionIcon.svg'
import { ReactComponent as ContentPollIcon } from '../../../assets/svg/ContentPollIcon.svg'
import { ANALYTICS_EVENTS } from '../../../analytics/events'
import useTrackedSurface from '../../../analytics/useTrackedSurface'

// Post first: it introduces the day, then the question and poll build on it.
const DAY_ITEM_ORDER = { feed: null, question: null, poll: null }
// Same icons as the type tabs on the post form, so the label reads the same there.
const DAY_ITEM_ICONS = { feed: ContentPostIcon, question: ContentQuestionIcon, poll: ContentPollIcon }

/** Quiet type label for a day item: icon + word, never louder than the item title. */
const DayItemType = ({ type, t }) => {
    const Icon = DAY_ITEM_ICONS[type]
    return (
        <p className="flex items-center gap-[6px] text-left text-sm font-medium text-deepPurple mb-3">
            {Icon && <Icon width={16} height={16} fill="currentColor" aria-hidden="true" />}
            {capitaliseFirstLetter(t(type))}
        </p>
    )
}

const clampDay = (day, length) => Math.min(Math.max(Number.isInteger(day) ? day : 0, 0), Math.max(length - 1, 0))

function SingleProgram({ programID, setScreen, initialDay = 0, onDayChange }) {
    let history = useHistory()
    const { t, locale } = useTranslation()
    const [contentItem, setContentItem] = useContext(ContentContext)
    const [showMore, setShowMore] = useState(false)
    const { user, setUser } = useUser()
    const [program, setProgram] = useState({ name: '', description: '' })
    const [currentDay, setCurrentDay] = useState(initialDay)
    const [loading, setLoading] = useState(false)
    const [days, setDays] = useState([{ poll: {}, question: {}, feed: {} }])
    const [showRun, setShowRun] = useState(false)
    const [showImg, setShowImg] = useState({ show: false, url: '' })
    const [programHistory, setProgramHistory] = useState([])
    const [historyID, setHistoryID] = useState([])
    const [showHistory, setShowHistory] = useState(false)
    const [currentHistory, setCurrentHistory] = useState([])
    const [selectedContent, setSelectedContent] = useState({
        msg: '',
        type: '',
        day: 0,
    })
    useTrackedSurface(ANALYTICS_EVENTS.CONTENT_LIBRARY_PROGRAM_VIEWED, {
        active: !!program?.id,
        contentId: program?.id || programID,
        content: program?.name,
    })

    const getProgram = async (ProgramID) => {
        setLoading(true)
        try {
            const response = await getById({ collection: 'programs', id: ProgramID })
            if (response.status === 200) {
                const { name, description, days, id } = response.data
                setProgram({ name, description, id })
                const list = Array.isArray(days) ? days : []
                setDays(list)
                setCurrentDay((day) => clampDay(day, list.length))
                scrollToTopOf(document.getElementById('programName'))
            } else {
                setProgram({})
                setDays([])
            }
        } catch (err) {
            console.error('SingleProgram: failed to load program', err)
            setProgram({})
            setDays([])
        } finally {
            setLoading(false)
        }
    }
    useEffect(() => {
        setCurrentDay(initialDay)
        getProgram(programID)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programID])
    const closeRun = () => {
        setShowRun(false)
    }
    const closeImg = () => {
        setShowImg({ show: false, url: '' })
    }
    const getHistory = async () => {
        try {
            const response = await getProgramHistory({
                programId: programID,
                institution_id: user?.institution_id,
            })
            if (response.status === 200) {
                const list = Array.isArray(response.data) ? response.data : []
                setHistoryID(list)
                setProgramHistory(
                    [...list]
                        .reverse()
                        .filter((thing, index, self) => index === self.findIndex((t) => t.programDetails?.dayIndex === thing.programDetails?.dayIndex && t?.type === thing?.type))
                )
            } else {
                setProgramHistory([])
                setHistoryID([])
            }
        } catch (err) {
            console.error('SingleProgram: failed to load history', err)
            setProgramHistory([])
            setHistoryID([])
        }
    }
    useEffect(() => {
        getHistory()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programID])
    const closeHistory = () => {
        setShowHistory(false)
    }
    const handleHistory = (msg, type, day) => {
        setCurrentHistory(historyID)
        setShowHistory(true)
        setSelectedContent({ msg: msg, type: type, day: day })
    }
    const handleSelect = (content, day) => {
        setContentItem({ ...content, programDetails: { dayIndex: day, programId: programID } })
        history.push(`/content?type=${content?.type}&singleProgram=true`)
    }
    const selectDay = (index) => {
        setCurrentDay(index)
        onDayChange?.(index)
    }
    const dayContent = days[currentDay] ?? {}
    return (
        <div className="rtg-detail px-14 p-8" id="programName">
            <div>
                <div className="rtg-detail-head flex items-center justify-between">
                    <div className="flex min-w-0 mr-4">
                        <button
                            type="button"
                            className="rtg-back-button flex pointer mr-5 items-center justify-center bg-[#5E639B] hover:bg-[#413F5E] min-w-[56px] w-[56px] h-[56px] rounded-full"
                            onClick={() => setScreen('home')}
                            aria-label={t('back')}
                        >
                            <TargetUndo />
                        </button>
                        <h1 className="rtg-detail-title font-bold text-deepPurple text-[40px]">
                            {loading && !program?.name ? <span className="rtg-skeleton-block inline-block h-[36px] w-[320px] align-middle" aria-hidden="true" /> : program?.name}
                        </h1>
                    </div>

                    <button
                        type="button"
                        id="run-program-button"
                        className="rtg-focus-ring rtg-run-button flex-shrink-0 text-highlightColor hover:text-deepPurple bg-deepPurple hover:bg-highlightColor rounded-full text-lg font-semibold pointer w-[300px] h-[65px] flex items-center justify-center"
                        onClick={() => setShowRun(true)}
                    >
                        {t('runProgram')}
                    </button>
                </div>
                <div>
                    {loading && !program?.description ? (
                        <div className="mt-5 mb-5" aria-hidden="true">
                            <div className="rtg-skeleton-block rtg-skeleton-line w-full" />
                            <div className="rtg-skeleton-block rtg-skeleton-line w-3/4" />
                        </div>
                    ) : (
                        <pre className="text-[#413F5E] text-base text-left mt-5 mb-5 whitespace-pre-wrap">{program?.description}</pre>
                    )}
                </div>
            </div>
            <div className="program_body_full" style={{ '&::-webkit-scrollbar': { height: '10px' } }}>
                <div className="program_days" style={{ display: 'block' }}>
                    <p className="text-left text-deepPurple font-bold text-lg mb-[11px]">{t('listOfDays')} </p>
                    <div className="rtg-day-grid pb-5">
                        {days.map((d, index) => {
                            return (
                                <button
                                    type="button"
                                    className={`rtg-day-chip rounded-[10px] pointer text-sm p-[10px] font-semibold flex items-center justify-center whitespace-nowrap ${
                                        index === currentDay ? 'bg-deepPurple text-highlightColor' : 'bg-lightBG hover:bg-deselectPurple text-deepPurple'
                                    }`}
                                    aria-pressed={index === currentDay}
                                    key={index}
                                    onClick={() => selectDay(index)}
                                >
                                    {t('day')} {(index + 1).toString()}
                                </button>
                            )
                        })}
                    </div>
                </div>
                <div className="selected_program_day rtg-panel-enter" key={currentDay}>
                    {Object.keys({ ...DAY_ITEM_ORDER, ...dayContent }).map((type, index) => {
                        return (
                            <div
                                className={`rtg-day-item bg-containerGray rounded-xl p-8 flex justify-between relative w-[32%] flex-col ${i18n.language == 'ar' ? 'text-right' : ''}`}
                                key={index}
                            >
                                <DayItemType type={type} t={t} />
                                <div className="widget_content mb-3">
                                    <button
                                        type="button"
                                        className="rtg-focus-ring rtg-day-action rtg-day-post w-full h-[40px] pointer border flex items-center justify-center border-deselectPurple hover:border-deepPurple rounded-full text-deepPurple text-sm font-semibold disabled:opacity-50 disabled:cursor-default disabled:hover:border-deselectPurple"
                                        disabled={loading || !dayContent[type]}
                                        onClick={() => {
                                            handleSelect(dayContent[type], currentDay)
                                        }}
                                    >
                                        {t('postIndividually')}
                                    </button>

                                    {programHistory.map((program, index) => {
                                        if (program?.programDetails?.dayIndex === +currentDay && program.type === type) {
                                            return (
                                                <button
                                                    type="button"
                                                    aria-label={`${t('lastCreated')} ${t(type)}: ${format(program.created_date, 'MM/dd/yyyy')}`}
                                                    className="rtg-focus-ring rtg-day-action rtg-day-history ml-2 pointer min-w-[40px] h-[40px] flex justify-center items-center rounded-full bg-mudPurple"
                                                    data-tip={`${t('lastCreated')} ${type}: ${format(program.created_date, 'MM/dd/yyyy')}`}
                                                    data-for="historyModal"
                                                    onClick={() => {
                                                        handleHistory(dayContent[type]?.message, type, +currentDay)
                                                    }}
                                                    key={index}
                                                >
                                                    <LastCreated aria-hidden="true" />
                                                    <ReactTooltip
                                                        id="historyModal"
                                                        arrowColor="transparent"
                                                        effect="solid"
                                                        backgroundColor="rgb(97,97,97)"
                                                        className="tooltip-small"
                                                    />
                                                </button>
                                            )
                                        }
                                        return null
                                    })}
                                </div>
                                {dayContent[type]?.files && (
                                    <div
                                        className="w-full mb-3 rounded-xl"
                                        onClick={() =>
                                            setShowImg({
                                                show: true,
                                                url: `${fileSystemUrl}/${dayContent[type]?.files.storage_name}?token=${user.token}`,
                                            })
                                        }
                                    >
                                        <div
                                            className="w-full rounded-xl h-[226px] pointer"
                                            style={{
                                                backgroundImage: `url(
                                   ${fileSystemUrl}/${dayContent[type]?.files.storage_name}?token=${user.token}
                                )`,
                                                backgroundSize: 'contain',
                                                backgroundPosition: 'center',
                                                backgroundRepeat: 'no-repeat',
                                            }}
                                        ></div>
                                    </div>
                                )}
                                <div
                                    className="widget_header "
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 0,
                                    }}
                                >
                                    <h4 className="text-left text-deepPurple text-xl font-bold mb-3">{dayContent[type]?.title}</h4>
                                    <div id="dot-line-program"></div>
                                </div>
                                <div className="flex flex-1">
                                    <div className={`w-full ${!(dayContent[type]?.message || dayContent[type]?.options) ? 'hidden' : ''}`}>
                                        <div className="w-full">
                                            {dayContent[type]?.message && (
                                                <pre className="w-full text-left text-darkenPurple text-sm mb-3 whitespace-pre-wrap">
                                                    {dayContent[type]?.message !== '' && <ReadMoreText message={dayContent[type]?.message} lines={10} className="w-full" />}
                                                </pre>
                                            )}
                                            {dayContent[type]?.options && (
                                                <div>
                                                    <p className="text-left font-bold text-deepPurple text-base flex justify-start">{t('pickOneOption')}:</p>
                                                    {dayContent[type]?.options.map((option, index) => {
                                                        return (
                                                            <div key={index} style={{ padding: '3px 0px' }} className="text-left flex items-center text-darkenPurple text-sm">
                                                                <span
                                                                    style={{
                                                                        paddingRight: '10px',
                                                                    }}
                                                                >
                                                                    <div className="w-[12px] h-[12px] rounded-full border-2 border-deepPurple"></div>
                                                                </span>
                                                                {option}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {dayContent[type]?.teacherMessage && i18n.language == 'en' && (
                                    <pre>
                                        {dayContent[type]?.teacherMessage !== '' && (
                                            <div className="teacherMessage text-left text-sm text-darkenPurple">
                                                <p className="text-base font-bold text-deepPurple text-left">{t('teachersOnly')}:</p>
                                                <ReadMoreText message={dayContent[type]?.teacherMessage} lines={10} />
                                            </div>
                                        )}
                                    </pre>
                                )}
                                {dayContent[type]?.url && <UrlPreview url={dayContent[type]?.url} />}
                            </div>
                        )
                    })}
                </div>
            </div>
            <RunProgramModal showRun={showRun} closeRun={closeRun} programDaysLength={days.length} programName={program?.name} user={user} programID={programID} />
            <ImagePreviewModal showImg={showImg.show} closeImg={closeImg} url={showImg.url} />
            <ProgramHistoryModal
                showHistory={showHistory}
                closeHistory={closeHistory}
                history={currentHistory}
                program={program}
                message={selectedContent.msg}
                type={selectedContent.type}
                day={selectedContent.day}
                programHistory={programHistory}
                setContentItem={setContentItem}
                setProgramHistory={setProgramHistory}
            />
        </div>
    )
}

export default SingleProgram
